import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { useEnvironment } from 'context/environment';
import Workflow, { CandidateFacility, WorkflowAction } from 'models/Workflow';
import { createPortalAiRun } from 'services/api/portalAiRuns';
import { indexPortalScripts, portalScriptQueryKeys, showPortalScript } from 'services/api/portalScripts';
import { WorkflowValuesInput } from 'services/api/workflows';
import { uploadPortalDocuments } from 'services/portalDocuments';
import {
  PortalRetrievalStatus,
  probePortalExtension,
  restartPortalRetrieval,
  runPortalRetrieval,
} from 'services/portalExtension';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { labelForValue } from './actionConfig';
import { sourceSystemLabel } from './constants';
import RetrievePortalForm from './RetrievePortalForm';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  onSaveProgress: (actionId: string, outputs: WorkflowValuesInput) => void;
  isSavingProgress: boolean;
}

// The value names whose retrieval outputs are documents rather than text — the
// engine's shared `attachments` value (captured via the extension's download marks,
// staged by the api as {id, filename} entries).
const DOCUMENT_OUTPUT_NAMES = ['attachments'];

// Render a staged output value: machine-collected values are usually strings, but
// stay defensive — arrays list out, document entries read as their filenames,
// absent reads as an em dash.
function formatStagedValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    return value
      .map((entry) =>
        entry && typeof entry === 'object' ? ((entry as { filename?: string }).filename ?? 'document') : String(entry)
      )
      .join(', ');
  }
  return String(value);
}

/**
 * The retrieval step's body, branched on the run's retrieval mode (OLIO-2605).
 * In priority order:
 *   1. staged outputs waiting on a person       -> confirm panel (values + facility pick)
 *   2. headless run in flight                   -> quiet progress line
 *   3. needs a person, manual mode              -> the phase-0 manual form
 *   4. needs a person, extension/headless mode  -> browser-extension hand-off
 *      (headless reaching action_required with nothing staged means it fell back)
 *   5. errored                                  -> the manual form as recovery
 */
export default function PortalRetrievalBody(props: Props) {
  const { action, workflow, onComplete, isCompleting } = props;
  const mode = workflow.retrievalMode;

  if (action.isActionRequired && action.outputs.length > 0) return <ConfirmPanel {...props} />;

  if (action.isRunning && mode === 'headless') {
    return (
      <Muted data-testid='portal-retrieval-progress'>
        Pulling from {sourceSystemLabel(workflow.valueFor('source_system')?.value ?? '')}…
      </Muted>
    );
  }

  if (action.isActionRequired && (mode === 'extension' || mode === 'headless')) return <HandOffFlow {...props} />;

  if (action.isActionRequired) {
    return (
      <RetrievePortalForm action={action} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
    );
  }

  if (action.isErrored) {
    return (
      <Flow>
        <Muted data-testid='portal-retrieval-errored'>Automated retrieval failed — enter the details manually.</Muted>
        <RetrievePortalForm action={action} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
      </Flow>
    );
  }

  return null;
}

// The staged values a retrieval collected, waiting on a person to pick the facility
// and confirm — confirming completes the action with the staged values as outputs.
function ConfirmPanel({ action, workflow, onComplete, isCompleting }: Props) {
  const candidates = workflow.candidateFacilities();
  const [facility, setFacility] = useState<CandidateFacility | null>(null);

  // A facility must be chosen when there are candidates to choose from; with none,
  // confirming sends the staged values alone (the kind resolves the group upstream).
  const ready = (candidates.length === 0 || !!facility) && !isCompleting;

  const confirm = () =>
    onComplete(action.id, {
      ...action.stagedOutputs(),
      ...(facility ? { candidate_group_ids: [facility.id] } : {}),
    });

  return (
    <Flow data-testid='portal-confirm-panel'>
      <BodySmall>Review the retrieved information, then confirm to continue.</BodySmall>
      <Staged>
        {action.outputs.map((output) => (
          <SummaryRow key={output.name} data-testid={`staged-output-${output.name}`}>
            <SummaryName>{output.label ?? labelForValue(workflow, output.name)}</SummaryName>
            <SummaryValue>{formatStagedValue(output.value)}</SummaryValue>
          </SummaryRow>
        ))}
      </Staged>
      {candidates.length > 0 && (
        <Field data-testid='portal-confirm-facility'>
          <FieldLabel>Facility</FieldLabel>
          <Select<CandidateFacility>
            inputId='portal-confirm-facility'
            placeholder='Select a facility'
            options={candidates}
            value={facility}
            onChange={setFacility}
            menuPortalTarget={document.body}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => (option.group_type ? `${option.group_type}: ${option.name}` : option.name)}
          />
        </Field>
      )}
      <div>
        <Button size='small' loading={isCompleting} disabled={!ready} onClick={confirm} data-testid='portal-confirm'>
          Confirm
        </Button>
      </div>
    </Flow>
  );
}

// The browser-extension hand-off: probe for the extension, then either replay the
// source system's saved script or record a fresh one against the portal link. The
// manual form stays one click away (and is the fallback whenever the hand-off can't run).
function HandOffFlow({ action, workflow, onComplete, isCompleting, onSaveProgress, isSavingProgress }: Props) {
  const { addToast } = useToastActions();
  const { environment } = useEnvironment();
  const [probed, setProbed] = useState<boolean | null>(null);
  const [running, setRunning] = useState(false);
  const [recording, setRecording] = useState(false);
  const [manual, setManual] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [liveStatus, setLiveStatus] = useState<PortalRetrievalStatus | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const sourceSystem = workflow.valueFor('source_system')?.value ?? '';
  const sourceLabel = sourceSystemLabel(sourceSystem);
  const retrievalPointer = workflow.valueFor('retrieval_pointer')?.value ?? '';
  const pointerIsUrl = /^https?:\/\//.test(retrievalPointer);

  useEffect(() => {
    let canceled = false;
    probePortalExtension().then((present) => {
      if (!canceled) setProbed(present);
    });
    return () => {
      canceled = true;
    };
  }, []);

  const scriptsQuery = useQuery({
    queryKey: portalScriptQueryKeys.list(sourceSystem),
    queryFn: ({ signal }) => indexPortalScripts(sourceSystem, signal),
    enabled: probed === true && !!sourceSystem,
  });
  const script = scriptsQuery.data?.[0];

  // Shared tail for every retrieval path: upload captured documents, guard the
  // document-first empty case, then complete or stage the values for confirmation.
  const handleValues = async (rawValues: Record<string, unknown>, documentOutputs: string[]) => {
    const values = await uploadPortalDocuments(rawValues, environment?.OLIO_FILES_DOMAIN);
    if (documentOutputs.length > 0 && documentOutputs.every((name) => !(values[name] as unknown[])?.length)) {
      setFailure('The portal run captured no documents — the script may need re-recording.');
      setManual(true);
      return;
    }
    const candidates = workflow.candidateFacilities();
    if (candidates.length === 1) {
      onComplete(action.id, { ...values, candidate_group_ids: [candidates[0].id] });
    } else {
      onSaveProgress(action.id, values);
    }
  };

  const run = async () => {
    setFailure(null);
    setRunning(true);
    setRecording(!script);
    try {
      // candidate_group_ids is a person's pick, never the extension's — don't ask for it.
      const expectedOutputs = action.produces.filter((name) => name !== 'candidate_group_ids');
      const documentOutputs = expectedOutputs.filter((name) => DOCUMENT_OUTPUT_NAMES.includes(name));
      const params = { patient_name: workflow.valueFor('portal_patient_name')?.value ?? '' };
      const context = { workflowId: workflow.id, actionId: action.id, sourceSystem, retrievalPointer };

      const onStatus = (status: PortalRetrievalStatus, id: string) => {
        setLiveStatus(status);
        setJobId(id);
      };

      let values: Record<string, unknown>;
      if (script) {
        const detail = await showPortalScript(script.id);
        values = await runPortalRetrieval(
          {
            script: { entryUrl: detail.entryUrl, body: detail.body, retrievalUrl: detail.retrievalUrl },
            ...(pointerIsUrl ? { entryUrl: retrievalPointer } : {}),
            params,
            context,
            expectedOutputs,
            documentOutputs,
          },
          { onStatus }
        );
      } else {
        values = await runPortalRetrieval(
          { record: true, entryUrl: retrievalPointer, params, context, expectedOutputs, documentOutputs },
          { onStatus }
        );
      }

      await handleValues(values, documentOutputs);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Portal retrieval failed.';
      addToast({ text: message });
      setFailure(message);
      setManual(true);
    } finally {
      setRunning(false);
      setLiveStatus(null);
      setJobId(null);
    }
  };

  // Record with AI: create a run, then hand it to the extension, whose side-panel
  // conductor drives the AI turns. The AI's action sequence records as the script.
  const runWithAi = async () => {
    setFailure(null);
    setRunning(true);
    setRecording(true);
    try {
      const expectedOutputs = action.produces.filter((name) => name !== 'candidate_group_ids');
      const documentOutputs = expectedOutputs.filter((name) => DOCUMENT_OUTPUT_NAMES.includes(name));
      const availableParams = { patient_name: workflow.valueFor('portal_patient_name')?.value ?? '' };
      const context = { workflowId: workflow.id, actionId: action.id, sourceSystem, retrievalPointer };
      const goal = { outputs: expectedOutputs };
      const onStatus = (status: PortalRetrievalStatus, id: string) => {
        setLiveStatus(status);
        setJobId(id);
      };

      const aiRun = await createPortalAiRun({ sourceSystem, goal, availableParams });
      const values = await runPortalRetrieval(
        {
          record: true,
          ai: true,
          runId: aiRun.id,
          entryUrl: retrievalPointer,
          params: availableParams,
          context,
          expectedOutputs,
          documentOutputs,
          goal,
          availableParams,
        },
        { onStatus }
      );
      await handleValues(values, documentOutputs);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Portal retrieval failed.';
      addToast({ text: message });
      setFailure(message);
      setManual(true);
    } finally {
      setRunning(false);
      setLiveStatus(null);
      setJobId(null);
    }
  };

  const restart = () => {
    if (jobId) restartPortalRetrieval(jobId);
    // The extension reruns the state machine from the top; its broadcasts repopulate this.
    setLiveStatus('running');
  };

  // Probing — resolves within half a second, nothing to flash in the meantime.
  if (probed === null) return null;

  const manualForm = (
    <RetrievePortalForm action={action} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
  );

  if (probed === false) {
    return (
      <Flow>
        <Muted data-testid='portal-extension-missing'>
          Olio browser extension not detected — enter the details manually.
        </Muted>
        {manualForm}
      </Flow>
    );
  }

  if (manual) {
    const badCredentials = !!failure && failure.includes('credentials appear invalid');
    return (
      <Flow>
        {badCredentials && (
          <Muted data-testid='portal-credentials-invalid'>
            The portal rejected the saved credentials. Update them in the extension side panel&apos;s Portal logins
            section, then try again.
          </Muted>
        )}
        {failure && !badCredentials && <Muted data-testid='portal-retrieval-failure'>{failure}</Muted>}
        {manualForm}
      </Flow>
    );
  }

  // The job may take minutes — record mode runs as long as the human takes in the portal tab.
  if (running || isSavingProgress || isCompleting) {
    if (liveStatus === 'confused') {
      return (
        <Flow>
          <Muted data-testid='portal-retrieval-confused'>The portal session got confused.</Muted>
          <div>
            <Button size='small' onClick={restart} data-testid='portal-retrieval-restart'>
              Restart
            </Button>
          </div>
        </Flow>
      );
    }
    if (liveStatus === 'waiting_login') {
      return (
        <Muted data-testid='portal-retrieval-waiting-login'>
          Log in to the portal tab to continue — the retrieval resumes automatically.
        </Muted>
      );
    }
    return (
      <Muted data-testid='portal-retrieval-running'>
        {recording ? 'Retrieving from the portal — finish in the portal tab…' : 'Retrieving…'}
      </Muted>
    );
  }

  if (scriptsQuery.isPending) return null;

  // Nothing to replay and nowhere to record — only the manual form can move this forward.
  if (!script && !pointerIsUrl) {
    return (
      <Flow>
        <Muted data-testid='portal-no-script'>No saved script and no portal link to record against.</Muted>
        {manualForm}
      </Flow>
    );
  }

  return (
    <Flow>
      <Actions>
        <Button size='small' onClick={run} data-testid='portal-retrieve'>
          {script ? `Retrieve from ${sourceLabel}` : `Record in ${sourceLabel}`}
        </Button>
        {!script && (
          <Button size='small' variant='outline-primary' onClick={runWithAi} data-testid='portal-retrieve-ai'>
            Record with AI
          </Button>
        )}
      </Actions>
      <ManualLink type='button' onClick={() => setManual(true)} data-testid='portal-enter-manually'>
        enter manually instead
      </ManualLink>
    </Flow>
  );
}

const Flow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Staged = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
`;

const SummaryName = styled(BodySmall)`
  color: ${colors.black50};
  flex-shrink: 0;
`;

const SummaryValue = styled(BodySmall)`
  color: ${colors.black};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 320px;
`;

const FieldLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

const ManualLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.primaryBlue};
  font-size: 12px;
  text-decoration: underline;
`;
